import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';

import { api } from '../../api';
import { BROKERAGE_ACCOUNT } from '../../constants';
import {
    filterTimeSlots,
    getOfficeDeliveryType,
    getAvailableDelivery,
    sentryLogError,
} from '../../utils';
import type {
    IFormattedAddressSuggestionData,
    IDeliveriesSearchResponse,
    ITimeSlot,
} from '../../models';

import type { TCreateThunkApi, IDeliverySlice } from './models';
import { setErrorStep, setRequestBrokerageAccount } from './application';
import { setSelectedTimeSlot } from './finish';
import { setAvailableAllProducts } from './basket';

const initialState: IDeliverySlice = {
    clientAddress: undefined,
    timeSlots: undefined,
    offices: undefined,
    courier: undefined,
    onlyCourierDelivery: undefined,
    availableDeliveries: undefined,
    deliveryScreenStatus: 'idle',
    deliveryType: 'courier',
    officeDeliveryType: 'any',
    isNeedReselectTimeSlot: false,
};

export const deliverySlice = createSlice({
    name: 'delivery',
    initialState,
    reducers: {
        setClientAddress: (state, action: PayloadAction<IDeliverySlice['clientAddress']>) => {
            state.clientAddress = action.payload;
        },
        setDelivery: (
            state,
            action: PayloadAction<
                Pick<IDeliverySlice, 'courier' | 'offices' | 'onlyCourierDelivery'>
            >
        ) => {
            const { courier, offices, onlyCourierDelivery } = action.payload;

            state.courier = courier;
            state.offices = offices;
            state.onlyCourierDelivery = onlyCourierDelivery;
        },
        setTimeSlots: (state, action: PayloadAction<IDeliverySlice['timeSlots']>) => {
            state.timeSlots = action.payload;
        },
        setOfficeDeliveryType: (
            state,
            action: PayloadAction<IDeliverySlice['officeDeliveryType']>
        ) => {
            state.officeDeliveryType = action.payload;
        },
        setOffices: (state, action: PayloadAction<IDeliverySlice['offices']>) => {
            state.offices = action.payload;
        },
        setAvailableDeliveries: (
            state,
            action: PayloadAction<IDeliverySlice['availableDeliveries']>
        ) => {
            state.availableDeliveries = action.payload;
        },
        setIsNeedReselectTimeSlot: (
            state,
            action: PayloadAction<IDeliverySlice['isNeedReselectTimeSlot']>
        ) => {
            state.isNeedReselectTimeSlot = action.payload;
        },
        setDeliveryScreenStatus: (
            state,
            action: PayloadAction<IDeliverySlice['deliveryScreenStatus']>
        ) => {
            state.deliveryScreenStatus = action.payload;
        },
        resetDelivery: () => ({ ...initialState }),
    },
});

export const {
    resetDelivery,
    setAvailableDeliveries,
    setClientAddress,
    setDelivery,
    setDeliveryScreenStatus,
    setIsNeedReselectTimeSlot,
    setOfficeDeliveryType,
    setOffices,
    setTimeSlots,
} = deliverySlice.actions;

/** Обновление стейта доступных вариантов доставки */
export const updateAvailableDeliveries = createAsyncThunk<void, undefined, TCreateThunkApi>(
    'updateAvailableDeliveries',
    (_, { dispatch, getState }) => {
        const {
            delivery: { courier, offices, onlyCourierDelivery, timeSlots },
        } = getState();

        const officeDeliveryType = getOfficeDeliveryType(offices);
        const availableDeliveries = getAvailableDelivery({
            timeSlots: timeSlots?.availableDates,
            courier,
            offices,
            onlyCourierDelivery,
        });

        dispatch(setAvailableDeliveries(availableDeliveries));
        dispatch(setOfficeDeliveryType(officeDeliveryType));
    }
);

/** Получение информации о доставке */
export const getDeliveryTypes = createAsyncThunk<void, undefined, TCreateThunkApi>(
    'getDeliveryTypes',
    async (_, { dispatch, getState }) => {
        const {
            application: { appId, businessUid, timeSlotDaysCount },
            basket: { products },
            delivery: { clientAddress },
        } = getState();
        let deliveries: IDeliveriesSearchResponse;

        if (
            !clientAddress?.fullName ||
            !(clientAddress?.cityKladrId || clientAddress?.regionKladrId)
        ) {
            sentryLogError({
                message: 'getDeliveryTypes error: Не получен адрес доставки или kladrId',
                data: { clientAddress, appId },
            });

            dispatch(setErrorStep());

            return;
        }

        const { cityKladrId, fullName, latitude, longitude, qcGeo, regionKladrId } = clientAddress;

        dispatch(setDeliveryScreenStatus('deliveriesLoading'));

        const requestData = {
            accuracy: Number(qcGeo),
            address: fullName,
            latitude: Number(latitude) || 0,
            longitude: Number(longitude) || 0,
            kladrId: cityKladrId || regionKladrId || '',
            appId,
            businessUid,
            selectedAdditionalCards: products,
        };

        try {
            const { data: deliveriesSearchData } = await api.deliveriesSearch(requestData);

            const { availableAdditionalCards, availableServices, ...otherData } =
                deliveriesSearchData;
            deliveries = otherData;

            const { availableDates, uuid, ...courier } = deliveries.courier;

            dispatch(
                setDelivery({
                    ...otherData,
                    courier,
                })
            );

            if (uuid && availableDates) {
                const filteredTimeSlots = filterTimeSlots(availableDates, timeSlotDaysCount);

                dispatch(
                    setTimeSlots({
                        uuid,
                        availableDates: filteredTimeSlots,
                    })
                );
            }

            dispatch(
                setAvailableAllProducts({
                    availableProducts: availableAdditionalCards || [],
                    availableServices: availableServices || [],
                })
            );

            const requestBrokerageAccount =
                deliveriesSearchData?.availableServices?.includes(BROKERAGE_ACCOUNT);

            dispatch(setRequestBrokerageAccount(requestBrokerageAccount));
        } catch (error) {
            sentryLogError({
                error,
                message: 'applicationsDeliveriesSearch error',
                data: { ...requestData, appId },
            });

            if (!axios.isAxiosError(error)) {
                dispatch(setErrorStep());

                return;
            }

            dispatch(setErrorStep(error?.response?.data?.status));

            return;
        }

        dispatch(updateAvailableDeliveries());
        dispatch(setDeliveryScreenStatus('idle'));
    }
);

/** Букирование тайм-слота */
export const bookTimeSlotRequest = createAsyncThunk<void, ITimeSlot, TCreateThunkApi>(
    'bookTimeSlotRequest',
    async (timeSlot, { dispatch, getState }) => {
        const {
            application: { appId, businessUid },
            delivery: { timeSlots },
        } = getState();

        if (!timeSlots?.uuid || !timeSlot) {
            sentryLogError({
                message: 'bookTimeSlotRequest error',
                data: { timeSlot, uuid: timeSlots?.uuid, appId },
            });

            return;
        }

        const timeSlotData = {
            ...timeSlot,
            uuid: timeSlots.uuid,
        };

        dispatch(setDeliveryScreenStatus('finishLoading'));

        const requestData = {
            appId,
            businessUid: businessUid,
            timeSlot: timeSlotData,
        };

        try {
            await api.bookTimeSlot(requestData);

            dispatch(setIsNeedReselectTimeSlot(false));
            dispatch(setSelectedTimeSlot(timeSlotData));
        } catch (error) {
            if (!axios.isAxiosError(error)) {
                dispatch(setErrorStep());

                sentryLogError({
                    error,
                    message: 'applicationsDeliveriesTimeSlotsPut error',
                    data: { ...requestData, appId },
                });

                return;
            }

            const status = error?.response?.data?.status;

            if (error?.response?.status === 410) {
                dispatch(getDeliveryTypes());
                dispatch(setIsNeedReselectTimeSlot(true));
            } else {
                dispatch(setErrorStep(status));

                sentryLogError({
                    error,
                    message: 'applicationsDeliveriesTimeSlotsPut error',
                    data: { ...requestData, appId },
                });
            }
        }

        dispatch(setDeliveryScreenStatus('idle'));
    }
);

/** Запись адреса клиента и последующее получение информации о доставке */
export const updateClientAddress = createAsyncThunk<
    void,
    IFormattedAddressSuggestionData,
    TCreateThunkApi
>('updateClientAddress', (addressData, { dispatch }) => {
    dispatch(setClientAddress(addressData));
    dispatch(getDeliveryTypes());
});
